import React, { useState, useEffect } from 'react';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Modal,
  Form,
  Input,
  message,
  Typography,
  Space,
  Button,
  Drawer,
} from 'antd';
import {
  HomeOutlined,
  KeyOutlined,
  ShopOutlined,
  ProjectOutlined,
  PhoneOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  BuildOutlined,
  TeamOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const { Sider, Header, Content } = Layout;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};
const { Text } = Typography;

const pageNames = {
  '/dashboard': 'AnaSayfa',
  '/kiralik': 'Kiralık İşlemler',
  '/kiralik/yeni': 'Yeni Kiralık',
  '/satilik': 'Satılık İşlemler',
  '/satilik/yeni': 'Yeni Satılık',
  '/kira-sozlesmesi': 'Kira Sözleşmeleri',
  '/satis-sozlesmesi': 'Satış Sözleşmeleri',
  '/aramalar': 'Arama Kayıtları',
  '/aramalar/yeni': 'Yeni Arama',
  '/sunumlar': 'Sunumlar',
  '/projeler': 'Proje Tanımları',
  '/projeler/yeni': 'Yeni Proje',
  '/proje-satis': 'Proje Satışları',
  '/proje-satis/yeni': 'Yeni Proje Satışı',
  '/kullanicilar': 'Kullanıcılar',
};

const AppLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  const getPageTitle = () => {
    const path = location.pathname;
    if (pageNames[path]) return pageNames[path];
    // Handle dynamic routes
    if (path.includes('/duzenle')) return 'Düzenle';
    if (path.includes('/kira-sozlesmesi/')) return 'Kira Sözleşmesi';
    if (path.includes('/satis-sozlesmesi/')) return 'Satış Sözleşmesi';
    return 'DekaPortal';
  };

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.startsWith('/kiralik')) return '/kiralik';
    if (path.startsWith('/satilik')) return '/satilik';
    if (path.startsWith('/kira-sozlesmesi')) return '/kira-sozlesmesi';
    if (path.startsWith('/satis-sozlesmesi')) return '/satis-sozlesmesi';
    if (path.startsWith('/aramalar')) return '/aramalar';
    if (path.startsWith('/sunumlar')) return '/sunumlar';
    if (path.startsWith('/projeler')) return '/projeler';
    if (path.startsWith('/proje-satis')) return '/proje-satis';
    if (path.startsWith('/kullanicilar')) return '/kullanicilar';
    return path;
  };

  const handlePasswordChange = async (values) => {
    if (values.yeniSifre !== values.yeniSifreTekrar) {
      message.error('Şifreler eşleşmiyor!');
      return;
    }
    setPasswordLoading(true);
    try {
      await api.put('/auth/change-password', { yeniSifre: values.yeniSifre });
      message.success('Şifre başarıyla değiştirildi');
      setPasswordModalOpen(false);
      form.resetFields();
    } catch (err) {
      message.error(err.response?.data?.message || 'Şifre değiştirilemedi');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'password',
      icon: <LockOutlined />,
      label: 'Şifre Değiştir',
      onClick: () => setPasswordModalOpen(true),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Çıkış',
      danger: true,
      onClick: handleLogout,
    },
  ];

  const buildMenuItems = () => {
    const items = [
      {
        key: '/dashboard',
        icon: <HomeOutlined />,
        label: 'AnaSayfa',
        onClick: () => navigate('/dashboard'),
      },
      {
        type: 'divider',
      },
      {
        key: 'gayrimenkul-group',
        type: 'group',
        label: 'Gayrimenkul İşlemleri',
        children: [
          {
            key: '/kiralik',
            icon: <KeyOutlined />,
            label: 'Kiralık İşlemler',
            onClick: () => navigate('/kiralik'),
          },
          {
            key: '/satilik',
            icon: <ShopOutlined />,
            label: 'Satılık İşlemler',
            onClick: () => navigate('/satilik'),
          },
          {
            key: '/proje-satis',
            icon: <ProjectOutlined />,
            label: 'Proje Satışları',
            onClick: () => navigate('/proje-satis'),
          },
          {
            key: '/aramalar',
            icon: <PhoneOutlined />,
            label: 'Arama Kayıtları',
            onClick: () => navigate('/aramalar'),
          },
          {
            key: '/sunumlar',
            icon: <VideoCameraOutlined />,
            label: 'Sunumlar',
            onClick: () => navigate('/sunumlar'),
          },
        ],
      },
    ];

    items.push({
      key: 'evrak-group',
      type: 'group',
      label: 'Evrak İşlemleri',
      children: [
        {
          key: '/kira-sozlesmesi',
          icon: <FileTextOutlined />,
          label: 'Kira Sözleşmeleri',
          onClick: () => navigate('/kira-sozlesmesi'),
        },
        {
          key: '/satis-sozlesmesi',
          icon: <FileDoneOutlined />,
          label: 'Satış Sözleşmeleri',
          onClick: () => navigate('/satis-sozlesmesi'),
        },
      ],
    });

    if (isAdmin()) {
      items.push({
        key: 'yonetim-group',
        type: 'group',
        label: 'Yönetim',
        children: [
          {
            key: '/projeler',
            icon: <BuildOutlined />,
            label: 'Proje Tanımları',
            onClick: () => navigate('/projeler'),
          },
          {
            key: '/kullanicilar',
            icon: <TeamOutlined />,
            label: 'Kullanıcılar',
            onClick: () => navigate('/kullanicilar'),
          },
        ],
      });
    }

    return items;
  };

  const sidebarMenu = (
    <>
      <div className="sidebar-logo">
        <img src="/logo.png" alt="logo" style={{ height: 36, objectFit: 'contain', flexShrink: 0 }} />
        {(!collapsed || isMobile) && (
          <span className="sidebar-logo-text">DekaPortal</span>
        )}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[getSelectedKey()]}
        items={buildMenuItems()}
        style={{ background: '#001529', border: 'none', paddingTop: 8 }}
        onClick={() => isMobile && setDrawerOpen(false)}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={240}
          styles={{ body: { padding: 0, background: '#001529' }, header: { display: 'none' } }}
        >
          {sidebarMenu}
        </Drawer>
      ) : (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={240}
          style={{
            background: '#001529',
            position: 'fixed',
            height: '100vh',
            left: 0,
            top: 0,
            bottom: 0,
            overflow: 'auto',
            zIndex: 100,
          }}
        >
          {sidebarMenu}
        </Sider>
      )}

      <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : 240), transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: '0 16px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,21,41,.08)',
            position: 'sticky',
            top: 0,
            zIndex: 99,
          }}
        >
          <Space>
            <Button
              type="text"
              icon={isMobile
                ? <MenuUnfoldOutlined />
                : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)
              }
              onClick={() => isMobile ? setDrawerOpen(true) : setCollapsed(!collapsed)}
              style={{ fontSize: 16 }}
            />
            <Text strong style={{ fontSize: 16 }}>
              {getPageTitle()}
            </Text>
          </Space>

          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <div className="header-user-area">
              <Avatar
                style={{ backgroundColor: '#1677ff' }}
                icon={<UserOutlined />}
              />
              <Space direction="vertical" size={0} style={{ lineHeight: 1.2 }}>
                <Text strong style={{ fontSize: 13 }}>{user?.adSoyad || 'Kullanıcı'}</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>{user?.rol}</Text>
              </Space>
            </div>
          </Dropdown>
        </Header>

        <Content
          style={{
            margin: isMobile ? '8px' : '16px',
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </Layout>

      <Modal
        title="Şifre Değiştir"
        open={passwordModalOpen}
        onCancel={() => { setPasswordModalOpen(false); form.resetFields(); }}
        footer={null}
        width={400}
      >
        <Form form={form} layout="vertical" onFinish={handlePasswordChange} style={{ marginTop: 16 }}>
          <Form.Item
            name="yeniSifre"
            label="Yeni Şifre"
            rules={[
              { required: true, message: 'Yeni şifre giriniz' },
              { min: 6, message: 'Şifre en az 6 karakter olmalıdır' },
            ]}
          >
            <Input.Password placeholder="Yeni şifre" />
          </Form.Item>
          <Form.Item
            name="yeniSifreTekrar"
            label="Yeni Şifre Tekrar"
            rules={[{ required: true, message: 'Şifreyi tekrar giriniz' }]}
          >
            <Input.Password placeholder="Şifreyi tekrar girin" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setPasswordModalOpen(false); form.resetFields(); }}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit" loading={passwordLoading}>
                Kaydet
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AppLayout;
